import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ColDef, ColumnState } from 'ag-grid-community';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { RadioButton } from 'primereact/radiobutton';
import { Toast } from 'primereact/toast';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import {
  ICustomViewProps,
  resetCustomViews,
  setCurrentPredefinedFilter,
  setCurrentViewId as setReduxCurrentViewId,
} from '@/app/slices/customViews';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  useAddView,
  useGetViewById,
  useGetViewNames,
  useRemoveView,
  useSetDefaultView,
  useUpdateView,
} from '@/services/custom-views/hooks';
import type { CustomView } from '@/services/custom-views/types';
import { DateRangeType, DynamicDateRangeType } from '@/types/dynamicDateRange';
import {
  calculateUnionDynamicDateRange,
  getDynamicDateRangeLabel,
} from '@/utils/dynamicDateRange';
import { getCircularReplacer } from '@/utils/getCirculatReplacer';

import { DynamicDateRangeSelect } from './DynamicDateRangeSelect';

type SavedColumn = Partial<ColumnState> &
  Pick<ColDef, 'field'> & { initialWidth?: number };

const savedColId = (col?: SavedColumn | string | null): string | undefined =>
  typeof col === 'string' ? col : (col?.colId ?? col?.field);

const toColumnState = (
  savedColumnDefs: SavedColumn[] = [],
  savedRowGroupColumns: (SavedColumn | string)[] = [],
): ColumnState[] => {
  const rowGroupIds = (savedRowGroupColumns ?? [])
    .map(savedColId)
    .filter((id): id is string => !!id);

  const state: ColumnState[] = [];

  for (const col of savedColumnDefs ?? []) {
    const colId = savedColId(col);
    if (!colId) continue;

    state.push({
      colId,
      hide: col.hide ?? false,
      pinned: col.pinned ?? null,
      sort: col.sort ?? null,
      sortIndex: col.sortIndex ?? null,
      width: col.width ?? col.initialWidth,
      rowGroup: col.rowGroup === true || rowGroupIds.includes(colId),
      rowGroupIndex: null,
    });
  }

  const orderOf = (colId?: string | null) => {
    const index = rowGroupIds.indexOf(colId ?? '');
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
  };

  state
    .filter((col) => col.rowGroup)
    .sort((a, b) => orderOf(a.colId) - orderOf(b.colId))
    .forEach((col, index) => {
      col.rowGroupIndex = index;
    });

  return state;
};

export const CustomViews = (props: ICustomViewProps) => {
  const siteFilterRef = (props as any).siteFilterRef;

  const me = useAppSelector((state) => state.authentication.me);
  const username = me?.email?.split('@')[0] ?? '';

  const dispatch = props.useAppDispatch();

  const isShippingDynamicDateEnabled = useFeatureFlag('Shipping_Dynamic_Date');
  const isSpecificViewContextEnabled = useFeatureFlag('Specific_View_Context');
  const preserveGridState = useFeatureFlag('Shipping_Preserve_Grid_State');
  const isFixCustomViews = useFeatureFlag('Fix_Custom_Views');
  const isShippingTodayFilterEnabled = useFeatureFlag(
    'Shipping_Integration_Today_Filter',
  );
  const viewContext = isSpecificViewContextEnabled
    ? props.viewContext
    : undefined;

  const toast = useRef<Toast>(null);
  const addViewRef = useRef<any>(null);
  const [addViewContainer, setAddViewContainer] = useState<HTMLElement>();

  const today = new Date();
  const [displayAddView, setDisplayAddView] = useState<boolean>(false);
  const [addViewName, setAddViewName] = useState<string>('');
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>('fixed');
  const [dynamicDateType, setDynamicDateType] = useState<
    DynamicDateRangeType[]
  >(['thisWeek']);
  const [selectedPredefinedFilter, setSelectedPredefinedFilter] = useState<
    string | null
  >(null);
  const [shareWithApp, setShareWithApp] = useState<boolean>(false);
  const [localCurrentViewId, setLocalCurrentViewId] = useState<string | null>(
    null,
  );
  const reduxCurrentViewId = useAppSelector(
    (state) => state.currentView.currentViewId ?? null,
  );
  const currentViewId = isSpecificViewContextEnabled
    ? localCurrentViewId
    : reduxCurrentViewId;
  const viewStorageKeyRef = useRef<string | null>(null);
  const appliedViewIdRef = useRef<string | null>(null);
  const appliedViewRef = useRef<CustomView | undefined>(undefined);
  const setCurrentViewId = useCallback(
    (id: string | null) => {
      if (isSpecificViewContextEnabled) {
        setLocalCurrentViewId(id);
      } else {
        dispatch(setReduxCurrentViewId(id));
      }
      if (isFixCustomViews && viewStorageKeyRef.current) {
        if (id) {
          localStorage.setItem(viewStorageKeyRef.current, id);
        } else {
          localStorage.removeItem(viewStorageKeyRef.current);
        }
      }
    },
    [isSpecificViewContextEnabled, dispatch, isFixCustomViews],
  );

  const location = useLocation();

  const hasCustomViewsWritePermission = useMemo(() => {
    return (
      me?.permissions?.some(
        (perm: any) => perm.code === 'CUSTOM_VIEWS.WRITE',
      ) ?? false
    );
  }, [me?.permissions]);

  const showPredefinedFilters = useMemo(() => {
    const path = location.pathname.toLowerCase();
    return path.includes('donor-scheduling') || path.includes('shipping');
  }, [location.pathname]);

  const dialogFuncMap = useMemo(
    () => ({
      displayAddView: setDisplayAddView,
    }),
    [],
  );

  const getDateFieldName = useCallback(() => {
    const currentPath = location.pathname.toLowerCase();

    switch (true) {
      case currentPath.includes('shipping'):
        return 'needByDate';
      case currentPath.includes('semen'):
        return 'donorScheduleDate';
      case currentPath.includes('donor-scheduling'):
        return 'date';
      default:
        return 'date';
    }
  }, [location.pathname]);

  const oneWeekToToday = useMemo(() => {
    const dateFieldName = getDateFieldName();
    return {
      [dateFieldName]: {
        dateFrom: format(subDays(today, 8), 'yyyy-MM-dd'),
        dateTo: format(addDays(today, 1), 'yyyy-MM-dd'),
        filterType: 'date',
        type: 'inRange',
      },
    };
  }, [getDateFieldName]);

  const twoWeekToOneWeek = useMemo(() => {
    const dateFieldName = getDateFieldName();
    return {
      [dateFieldName]: {
        dateFrom: format(subDays(today, 15), 'yyyy-MM-dd'),
        dateTo: format(subDays(today, 6), 'yyyy-MM-dd'),
        filterType: 'date',
        type: 'inRange',
      },
    };
  }, [getDateFieldName]);

  const todayFilter = useMemo(() => {
    const dateFieldName = getDateFieldName();
    return {
      [dateFieldName]: isShippingTodayFilterEnabled
        ? {
            dateFrom: format(today, 'yyyy-MM-dd'),
            dateTo: null,
            filterType: 'date',
            type: 'equals',
          }
        : {
            dateFrom: format(today, 'yyyy-MM-dd'),
            dateTo: format(addDays(today, 1), 'yyyy-MM-dd'),
            filterType: 'date',
            type: 'inRange',
          },
    };
  }, [getDateFieldName, isShippingTodayFilterEnabled]);

  const tomorrowFilter = useMemo(() => {
    const dateFieldName = getDateFieldName();
    return {
      [dateFieldName]: isShippingTodayFilterEnabled
        ? {
            dateFrom: format(addDays(today, 1), 'yyyy-MM-dd'),
            dateTo: null,
            filterType: 'date',
            type: 'equals',
          }
        : {
            dateFrom: format(addDays(today, 1), 'yyyy-MM-dd'),
            dateTo: format(addDays(today, 2), 'yyyy-MM-dd'),
            filterType: 'date',
            type: 'inRange',
          },
    };
  }, [getDateFieldName, isShippingTodayFilterEnabled]);

  const yesterdayFilter = useMemo(() => {
    const dateFieldName = getDateFieldName();
    return {
      [dateFieldName]: isShippingTodayFilterEnabled
        ? {
            dateFrom: format(subDays(today, 1), 'yyyy-MM-dd'),
            dateTo: null,
            filterType: 'date',
            type: 'equals',
          }
        : {
            dateFrom: format(subDays(today, 1), 'yyyy-MM-dd'),
            dateTo: format(today, 'yyyy-MM-dd'),
            filterType: 'date',
            type: 'inRange',
          },
    };
  }, [getDateFieldName, isShippingTodayFilterEnabled]);

  const thisWeekFilter = useMemo(() => {
    const dateFieldName = getDateFieldName();

    const startOfThisWeek = startOfWeek(today, { weekStartsOn: 1 });
    const endOfThisWeek = endOfWeek(today, { weekStartsOn: 1 });
    return {
      [dateFieldName]: {
        dateFrom: format(startOfThisWeek, 'yyyy-MM-dd'),
        dateTo: format(endOfThisWeek, 'yyyy-MM-dd'),
        filterType: 'date',
        type: 'inRange',
      },
    };
  }, [getDateFieldName]);

  const nextWeekFilter = useMemo(() => {
    const dateFieldName = getDateFieldName();
    const startOfNextWeek = startOfWeek(addWeeks(today, 1), {
      weekStartsOn: 1,
    });
    const endOfNextWeek = endOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
    return {
      [dateFieldName]: {
        dateFrom: format(startOfNextWeek, 'yyyy-MM-dd'),
        dateTo: format(endOfNextWeek, 'yyyy-MM-dd'),
        filterType: 'date',
        type: 'inRange',
      },
    };
  }, [getDateFieldName]);

  const lastWeekFilter = useMemo(() => {
    const dateFieldName = getDateFieldName();
    const startOfLastWeek = startOfWeek(subWeeks(today, 1), {
      weekStartsOn: 1,
    });
    const endOfLastWeek = endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
    return {
      [dateFieldName]: {
        dateFrom: format(startOfLastWeek, 'yyyy-MM-dd'),
        dateTo: format(endOfLastWeek, 'yyyy-MM-dd'),
        filterType: 'date',
        type: 'inRange',
      },
    };
  }, [getDateFieldName]);

  const nextMonthFilter = useMemo(() => {
    const dateFieldName = getDateFieldName();
    const startOfNextMonth = startOfMonth(addMonths(today, 1));
    const endOfNextMonth = endOfMonth(addMonths(today, 1));
    return {
      [dateFieldName]: {
        dateFrom: format(startOfNextMonth, 'yyyy-MM-dd'),
        dateTo: format(endOfNextMonth, 'yyyy-MM-dd'),
        filterType: 'date',
        type: 'inRange',
      },
    };
  }, [getDateFieldName]);

  const thisMonthFilter = useMemo(() => {
    const dateFieldName = getDateFieldName();
    const startOfThisMonth = startOfMonth(today);
    const endOfThisMonth = endOfMonth(today);
    return {
      [dateFieldName]: {
        dateFrom: format(startOfThisMonth, 'yyyy-MM-dd'),
        dateTo: format(endOfThisMonth, 'yyyy-MM-dd'),
        filterType: 'date',
        type: 'inRange',
      },
    };
  }, [getDateFieldName]);

  const lastMonthFilter = useMemo(() => {
    const dateFieldName = getDateFieldName();
    const startOfLastMonth = startOfMonth(subMonths(today, 1));
    const endOfLastMonth = endOfMonth(subMonths(today, 1));
    return {
      [dateFieldName]: {
        dateFrom: format(startOfLastMonth, 'yyyy-MM-dd'),
        dateTo: format(endOfLastMonth, 'yyyy-MM-dd'),
        filterType: 'date',
        type: 'inRange',
      },
    };
  }, [getDateFieldName]);

  const hasDateFilter = useCallback(() => {
    const currentFilterModel = props.api.getFilterModel();
    return !!(
      currentFilterModel.date ||
      currentFilterModel.donorScheduleDate ||
      currentFilterModel.needByDate
    );
  }, [props.api]);

  const isPredefinedFilterSelected = useCallback(
    (filterName: string) => {
      return selectedPredefinedFilter === filterName;
    },
    [selectedPredefinedFilter],
  );

  const onClick = useCallback(
    (name: keyof typeof dialogFuncMap) => {
      if (name === 'displayAddView') {
        if (hasDateFilter()) {
          setDateRangeType('dynamic');
          setDynamicDateType(['thisWeek']);
        } else {
          setDateRangeType('fixed');
          setDynamicDateType(['thisWeek']);
        }
      }
      dialogFuncMap[`${name}`](true);
    },
    [dialogFuncMap, hasDateFilter],
  );

  const onHide = useCallback(
    (name: keyof typeof dialogFuncMap) => {
      dialogFuncMap[`${name}`](false);
    },
    [dialogFuncMap],
  );

  const reject = useCallback(() => {
    toast.current?.show({
      severity: 'warn',
      summary: 'Rejected',
      detail: 'You have rejected',
      life: 3000,
    });
  }, []);

  const [queryParams] = useSearchParams();
  const parts = useMemo(
    () => location.pathname.split('/'),
    [location.pathname],
  );
  const primary: string = viewContext ?? parts[1]?.toLowerCase() ?? '';
  const secondary: string = viewContext ? '' : (parts[2]?.toLowerCase() ?? '');
  const ranch: string = viewContext ? '' : (queryParams.get('ranch') ?? '');
  const tabIndex: string = viewContext
    ? '0'
    : (queryParams.get('tabIndex') ?? '0');
  const routePath = `primary=${primary}&secondary=${secondary}&ranch=${ranch}&tabIndex=${tabIndex}`;

  //this is used to store the current view id in local storage for Fix_Custom_Views feature flag
  const viewStorageKey = isFixCustomViews
    ? `custom-view|${import.meta.env.VITE_APP_NAME}|${routePath}`
    : null;
  viewStorageKeyRef.current = viewStorageKey;

  const viewStorageInitRef = useRef(false);
  useEffect(() => {
    if (!isFixCustomViews || !viewStorageKey || viewStorageInitRef.current)
      return;
    viewStorageInitRef.current = true;
    const stored = localStorage.getItem(viewStorageKey);
    if (stored) {
      setCurrentViewId(stored);
    }
  }, [isFixCustomViews, viewStorageKey, setCurrentViewId]);

  const { data: viewList = [] } = useGetViewNames({
    username: username ?? '',
    usage: `app=${import.meta.env.VITE_APP_NAME}&` + routePath,
    app: primary,
  });

  const { data: selectedView, refetch: refetchGetViewById } = useGetViewById(
    currentViewId ?? undefined,
    { enabled: !!currentViewId },
  );

  useEffect(() => {
    props.api.resetColumnState();
    props.api.applyColumnState({
      defaultState: { sort: null, rowGroup: false },
      applyOrder: true,
    });
    props.api.setFilterModel({});

    return () => {
      dispatch(resetCustomViews());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePath]);

  useEffect(() => {
    if (isFixCustomViews) {
      if (!currentViewId) {
        appliedViewIdRef.current = null;
        return;
      }
      if (!selectedView || selectedView.id !== currentViewId) return;
      if (appliedViewIdRef.current === currentViewId) return;
      appliedViewIdRef.current = currentViewId;
    } else if (selectedView === appliedViewRef.current) {
      return;
    }
    appliedViewRef.current = selectedView;

    if (selectedView) {
      setSelectedPredefinedFilter(null);

      resetColumns();
      const json = JSON.parse(selectedView.view);

      if (isFixCustomViews || preserveGridState) {
        props.api.resetColumnGroupState();
        props.api.applyColumnState({
          state: toColumnState(json.columnDefs, json.rowGroupColumns),
          applyOrder: true,
        });
      } else {
        const cd: any[] = json.columnDefs.map((x: any) =>
          props.api
            .getColumnDefs()!
            .find((y) => 'colId' in y && y.colId === x.colId),
        );
        const columnDefs: any[] = json.columnDefs.map(
          (x: ColDef, index: number) => {
            return { ...cd[index], ...x };
          },
        );

        props.api.resetColumnGroupState();
        props.api.setRowGroupColumns(json.rowGroupColumns);
        props.api.setGridOption('columnDefs', columnDefs);
      }

      let finalFilterModel = json.filterModel;

      if (
        json.dateRange &&
        json.dateRange.type === 'dynamic' &&
        (json.dateRange.dynamicTypes || json.dateRange.dynamicType)
      ) {
        const types: DynamicDateRangeType[] =
          json.dateRange.dynamicTypes ??
          (json.dateRange.dynamicType
            ? [json.dateRange.dynamicType]
            : ['thisWeek']);
        const dynamicDates = calculateUnionDynamicDateRange(
          types,
          json.dateRange.originalStart,
          json.dateRange.originalEnd,
        );

        const dateFieldName = getDateFieldName();
        if (finalFilterModel && finalFilterModel[dateFieldName]) {
          const isSingleDay =
            isShippingTodayFilterEnabled &&
            dynamicDates.dateFrom === dynamicDates.dateTo;
          finalFilterModel = {
            ...finalFilterModel,
            [dateFieldName]: {
              ...finalFilterModel[dateFieldName],
              dateFrom: dynamicDates.dateFrom,
              dateTo: isSingleDay ? null : dynamicDates.dateTo,
              ...(isShippingTodayFilterEnabled && {
                type: isSingleDay ? 'equals' : 'inRange',
              }),
            },
          };
        }
      }

      const paginationEnabled = props.api.paginationGetRowCount();
      if (paginationEnabled === 1) {
        if (finalFilterModel && typeof finalFilterModel === 'object') {
          const validFilterModel = Object.entries(finalFilterModel).reduce<{
            [key: string]: any;
          }>((acc, [key, value]) => {
            if (value && typeof value === 'object') {
              const isTextFilter = 'type' in value;
              const isSetFilter = 'values' in value || 'filterType' in value;

              if (isTextFilter || isSetFilter) {
                acc[key] = value;
              }
            }
            return acc;
          }, {});

          props.api.setFilterModel(validFilterModel);
        } else {
          props.api.setFilterModel({});
        }
      } else {
        props.api.setFilterModel(finalFilterModel);
      }

      if (typeof json.quickFilter === 'string') {
        props.api.setGridOption('quickFilterText', json.quickFilter);
        dispatch(props.setQuickFilter(json.quickFilter || ''));
      }

      if (json.sites?.length && siteFilterRef?.current?.setSites) {
        siteFilterRef.current.setSites(json.sites);
      } else if (siteFilterRef?.current?.setSites) {
        siteFilterRef.current.setSites([]);
      }
    }
  }, [selectedView, currentViewId, isFixCustomViews, preserveGridState]);

  useEffect(() => {
    const storedPredefinedFilter = localStorage.getItem(
      'ag-grid-predefined-filter',
    );
    if (storedPredefinedFilter && !selectedPredefinedFilter) {
      setSelectedPredefinedFilter(storedPredefinedFilter);
      dispatch(setCurrentPredefinedFilter(storedPredefinedFilter));
    }
  }, [dispatch, selectedPredefinedFilter]);

  const { mutateAsync: addView } = useAddView();
  const { mutateAsync: updateView } = useUpdateView();
  const { mutateAsync: removeView } = useRemoveView();
  const { mutateAsync: defaultView } = useSetDefaultView();

  const AddFooter = useCallback(
    (name: keyof typeof dialogFuncMap) => {
      const quickFilter = props.api.getQuickFilter();
      const add = (name: string) => {
        let columnDefs;
        let rowGroupColumns;

        // If Fix_Custom_Views is enabled, we will save the column state with rowGroup and rowGroupIndex properties.
        // Otherwise, we will save the column state without rowGroup and rowGroupIndex properties.
        if (isFixCustomViews) {
          columnDefs = props.api.getColumnState().map((col: ColumnState) => ({
            colId: col.colId,
            hide: col.hide,
            pinned: col.pinned,
            sort: col.sort,
            sortIndex: col.sortIndex,
            width: col.width,
            rowGroup: col.rowGroup,
            rowGroupIndex: col.rowGroupIndex,
          }));
          rowGroupColumns = columnDefs
            .filter((c: { rowGroup?: boolean | null }) => c.rowGroup === true)
            .sort(
              (
                a: { rowGroupIndex?: number | null },
                b: { rowGroupIndex?: number | null },
              ) => (a.rowGroupIndex ?? 0) - (b.rowGroupIndex ?? 0),
            )
            .map((c: { colId?: string }) => c.colId);
        } else {
          const gridColumns = props.api.getAllGridColumns();
          columnDefs = props.api
            .getColumnDefs()
            ?.map((col: ColDef, index: number) => {
              const width = gridColumns[index].getActualWidth();

              return {
                colId: col.colId,
                hide: col.hide,
                pinned: col.pinned,
                sort: col.sort,
                sortIndex: col.sortIndex,
                width,
              };
            });
          rowGroupColumns = props.columnApi.getRowGroupColumns();
        }

        const currentFilterModel = props.api.getFilterModel();
        let finalFilterModel = currentFilterModel;

        if (hasDateFilter()) {
          if (dateRangeType === 'dynamic') {
            const dateFieldName = getDateFieldName();
            const dateFilter = currentFilterModel[dateFieldName];
            const originalStart = dateFilter.dateFrom;
            const originalEnd = dateFilter.dateTo;

            const dynamicDates = calculateUnionDynamicDateRange(
              dynamicDateType,
              originalStart,
              originalEnd,
            );

            const isSingleDay =
              isShippingTodayFilterEnabled &&
              dynamicDates.dateFrom === dynamicDates.dateTo;

            finalFilterModel = {
              ...currentFilterModel,
              [dateFieldName]: {
                ...dateFilter,
                dateFrom: dynamicDates.dateFrom,
                dateTo: isSingleDay ? null : dynamicDates.dateTo,
                ...(isShippingTodayFilterEnabled && {
                  type: isSingleDay ? 'equals' : 'inRange',
                }),
              },
            };
          }
        }

        const currentSites =
          siteFilterRef?.current?.getSites?.().map((s: any) => ({
            id: s.id,
            name: s.name,
          })) ?? [];

        // use rowGroup columns because columnApi is deprecated
        const viewData = {
          columnDefs,
          filterModel: finalFilterModel,
          quickFilter,
          rowGroupColumns: isFixCustomViews
            ? rowGroupColumns
            : props.columnApi.getRowGroupColumns(),
          ...(hasDateFilter() && {
            dateRange: {
              type: dateRangeType,
              dynamicTypes:
                dateRangeType === 'dynamic' ? dynamicDateType : undefined,
              fixedStart:
                dateRangeType === 'fixed'
                  ? currentFilterModel[getDateFieldName()]?.dateFrom
                  : undefined,
              fixedEnd:
                dateRangeType === 'fixed'
                  ? currentFilterModel[getDateFieldName()]?.dateTo
                  : undefined,
              originalStart: currentFilterModel[getDateFieldName()]?.dateFrom,
              originalEnd: currentFilterModel[getDateFieldName()]?.dateTo,
            },
          }),
          ...(currentSites.length > 0 && { sites: currentSites }),
        };

        addView({
          name,
          username: username ?? '',
          usage: `app=${import.meta.env.VITE_APP_NAME}&${routePath}`,
          view: JSON.stringify(viewData, getCircularReplacer()),
          app: shareWithApp ? primary : undefined,
        })
          .then((payload) => {
            toast.current?.show({
              severity: 'success',
              summary: 'Save',
              detail: 'The new view has been saved.',
              life: 3000,
            });
            onHide('displayAddView');
            if (payload.id) setCurrentViewId(payload.id);
            setAddViewName('');
            setDateRangeType('fixed');
            setDynamicDateType(['thisWeek']);
            setShareWithApp(false);
          })
          .catch(() => {});
      };

      const cancel = () => {
        setAddViewName('');
        setDateRangeType('fixed');
        setDynamicDateType(['thisWeek']);
        setShareWithApp(false);
        onHide(name);
      };

      return (
        <div>
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={cancel}
            className="p-button-text"
          />
          <Button
            label="Add"
            icon="pi pi-check"
            onClick={() => add(addViewName)}
            disabled={isShippingDynamicDateEnabled && !addViewName.trim()}
            autoFocus
          />
        </div>
      );
    },
    [
      addView,
      addViewName,
      routePath,
      dispatch,
      onHide,
      props.api,
      props.columnApi,
      username,
      dateRangeType,
      dynamicDateType,
      hasDateFilter,
      getDateFieldName,
      shareWithApp,
      isFixCustomViews,
    ],
  );

  const showUpdateViewDialog = () => {
    const quickFilter = props.api.getQuickFilter();
    confirmDialog({
      message: 'Are you sure you wish to update the current view?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        let columnDefs;
        let rowGroupColumns;

        // If Fix_Custom_Views is enabled, we will save the column state with rowGroup and rowGroupIndex properties.
        // Otherwise, we will save the column state without rowGroup and rowGroupIndex properties.
        if (isFixCustomViews) {
          columnDefs = props.api.getColumnState().map((col: ColumnState) => ({
            colId: col.colId,
            hide: col.hide,
            pinned: col.pinned,
            sort: col.sort,
            sortIndex: col.sortIndex,
            width: col.width,
            rowGroup: col.rowGroup,
            rowGroupIndex: col.rowGroupIndex,
          }));
          rowGroupColumns = columnDefs
            .filter((c: { rowGroup?: boolean | null }) => c.rowGroup === true)
            .sort(
              (
                a: { rowGroupIndex?: number | null },
                b: { rowGroupIndex?: number | null },
              ) => (a.rowGroupIndex ?? 0) - (b.rowGroupIndex ?? 0),
            )
            .map((c: { colId?: string }) => c.colId);
        } else {
          const gridColumns = props.columnApi.getAllGridColumns();
          columnDefs = props.api
            .getColumnDefs()
            ?.map((col: any, index: number) => {
              const width = gridColumns[index].getActualWidth();

              if (preserveGridState) {
                return {
                  colId: col.colId,
                  hide: col.hide,
                  pinned: col.pinned,
                  sort: col.sort,
                  sortIndex: col.sortIndex,
                  rowGroup: col.rowGroup,
                  rowGroupIndex: col.rowGroupIndex,
                  width,
                };
              }

              return {
                ...col,
                initialWidth: width,
              };
            });
          rowGroupColumns = props.columnApi.getRowGroupColumns();
        }

        const updateSites =
          siteFilterRef?.current?.getSites?.().map((s: any) => ({
            id: s.id,
            name: s.name,
          })) ?? [];

        // If Fix_Custom_Views is enabled, we will preserve the existing dateRange (for dynamic view) in the view if it exists.
        const existingDateRange = (() => {
          if (!isFixCustomViews || !selectedView?.view) return {};
          try {
            const existing = JSON.parse(selectedView.view);
            if (!existing.dateRange) return {};
            const dateFieldName = getDateFieldName();
            const currentFilterModel = props.api.getFilterModel();
            if (!currentFilterModel[dateFieldName]) return {};
            return { dateRange: existing.dateRange };
          } catch {
            return {};
          }
        })();

        updateView({
          id: currentViewId!,
          view: JSON.stringify(
            {
              columnDefs,
              filterModel: props.api.getFilterModel(),
              quickFilter,
              rowGroupColumns: isFixCustomViews
                ? rowGroupColumns
                : props.columnApi.getRowGroupColumns(),
              ...(updateSites.length > 0 && { sites: updateSites }),
              ...existingDateRange,
            },
            getCircularReplacer(),
          ),
        })
          .then(() => {
            refetchGetViewById();
            toast.current?.show({
              severity: 'success',
              summary: 'Updated',
              detail: 'The current view has been updated.',
              life: 3000,
            });
            onHide('displayAddView');
          })
          .catch(() => {
            reject();
          });
      },
    });
  };

  const showDeleteViewDialog = (id: string) => {
    confirmDialog({
      message: 'Are you sure you want to proceed?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        removeView(id)
          .then(() => {
            toast.current?.show({
              severity: 'success',
              summary: 'Save',
              detail: 'The new view has been saved.',
              life: 3000,
            });
            onHide('displayAddView');
          })
          .catch(() => {
            reject();
          });
      },
    });
  };

  const focusOnFirstInput = () => {
    addViewRef.current.getContent().querySelector('input').focus();
  };

  const handleSelectView = (id: string) => {
    setSelectedPredefinedFilter(null);
    dispatch(setCurrentPredefinedFilter(undefined));

    localStorage.removeItem('ag-grid-predefined-filter');

    // If Fix_Custom_Views is enabled, we will reset the columns and filters when the current view is deselected.
    if (isFixCustomViews && id === currentViewId) {
      resetColumns();
      dispatch(props.setQuickFilter(''));
      dispatch(resetCustomViews());
      setCurrentViewId(null);
      if (siteFilterRef?.current?.setSites) {
        siteFilterRef.current.setSites([]);
      }
      setTimeout(() => {
        props.api?.refreshCells();
      }, 100);
      return;
    }

    props.api?.setFilterModel({});

    setCurrentViewId(id);

    setTimeout(() => {
      props.api?.refreshCells();
    }, 100);
  };

  const transformFilterForCurrentApp = useCallback(
    (filterModel: any) => {
      const dateFieldName = getDateFieldName();

      if (filterModel.date && dateFieldName === 'donorScheduleDate') {
        const { date, ...rest } = filterModel;
        return {
          ...rest,
          donorScheduleDate: date,
        };
      }
      if (filterModel.date && dateFieldName === 'needByDate') {
        const { date, ...rest } = filterModel;
        return {
          ...rest,
          needByDate: date,
        };
      }
      return filterModel;
    },
    [getDateFieldName],
  );

  const handlePredefinedFilterClick = (
    filterName: string,
    filterModel: any,
  ) => {
    props.api?.setFilterModel({});

    const transformedFilter = transformFilterForCurrentApp(filterModel);
    props.api?.setFilterModel(transformedFilter);

    setSelectedPredefinedFilter(filterName);
    setCurrentViewId(null);
    dispatch(setCurrentPredefinedFilter(filterName));

    localStorage.setItem('ag-grid-predefined-filter', filterName);

    setTimeout(() => {
      props.api?.refreshCells();
    }, 100);
  };

  const resetColumns = () => {
    props.api.applyColumnState({
      defaultState: { sort: null, rowGroup: false },
      applyOrder: true,
    });
    props.api.setFilterModel({});
    props.api.setGridOption('quickFilterText', '');
    props.api.resetColumnState();
  };

  const handleClearAllFilters = () => {
    resetColumns();
    dispatch(props.setQuickFilter(''));
    dispatch(resetCustomViews());
    dispatch(setCurrentPredefinedFilter(undefined));
    setSelectedPredefinedFilter(null);
    if (isFixCustomViews) {
      setCurrentViewId(null);
    }

    localStorage.removeItem('ag-grid-predefined-filter');
    props.onClearAllFilters?.();

    if (siteFilterRef?.current?.setSites) {
      siteFilterRef.current.setSites([]);
    }

    setTimeout(() => {
      props.api?.refreshCells();
    }, 100);
  };

  const handleClearLocalStorage = useCallback(() => {
    localStorage.clear();
    window.location.reload();
  }, []);

  const handleDefaultView = (viewId: string) => {
    defaultView({
      id: viewId,
      username: username ?? '',
      usage: `${import.meta.env.VITE_USAGE_APP}|${routePath}`,
    });
  };

  return (
    <div className="w-full text-sm">
      <Toast ref={toast} />

      <div className="max-h-[calc(100vh-374px)] overflow-y-auto border-b border-gray-300 bg-gray-50 p-4">
        <div className="mb-2 border-b border-gray-300 pb-2 font-bold">
          My Views
        </div>
        {showPredefinedFilters && (
          <div className="space-y-2">
            <div
              className={`flex cursor-pointer items-center gap-2 rounded hover:bg-gray-100 ${
                isPredefinedFilterSelected('oneWeekToToday')
                  ? 'border border-blue-200 bg-blue-50 font-bold'
                  : ''
              }`}
              onClick={() =>
                handlePredefinedFilterClick('oneWeekToToday', oneWeekToToday)
              }
            >
              <i className="pi pi-eye text-gray-500"></i>
              <span>1 Week to Today</span>
            </div>
            <div
              className={`flex cursor-pointer items-center gap-2 rounded hover:bg-gray-100 ${
                isPredefinedFilterSelected('twoWeekToOneWeek')
                  ? 'border border-blue-200 bg-blue-50 font-bold'
                  : ''
              }`}
              onClick={() =>
                handlePredefinedFilterClick(
                  'twoWeekToOneWeek',
                  twoWeekToOneWeek,
                )
              }
            >
              <i className="pi pi-eye text-gray-500"></i>
              <span>2 Week to 1 Week</span>
            </div>
            <div
              className={`flex cursor-pointer items-center gap-2 rounded hover:bg-gray-100 ${
                isPredefinedFilterSelected('today')
                  ? 'border border-blue-200 bg-blue-50 font-bold'
                  : ''
              }`}
              onClick={() => handlePredefinedFilterClick('today', todayFilter)}
            >
              <i className="pi pi-eye text-gray-500"></i>
              <span>Today</span>
            </div>
            <div
              className={`flex cursor-pointer items-center gap-2 rounded hover:bg-gray-100 ${
                isPredefinedFilterSelected('tomorrow')
                  ? 'border border-blue-200 bg-blue-50 font-bold'
                  : ''
              }`}
              onClick={() =>
                handlePredefinedFilterClick('tomorrow', tomorrowFilter)
              }
            >
              <i className="pi pi-eye text-gray-500"></i>
              <span>Tomorrow</span>
            </div>
            <div
              className={`flex cursor-pointer items-center gap-2 rounded hover:bg-gray-100 ${
                isPredefinedFilterSelected('yesterday')
                  ? 'border border-blue-200 bg-blue-50 font-bold'
                  : ''
              }`}
              onClick={() =>
                handlePredefinedFilterClick('yesterday', yesterdayFilter)
              }
            >
              <i className="pi pi-eye text-gray-500"></i>
              <span>Yesterday</span>
            </div>
            <div
              className={`flex cursor-pointer items-center gap-2 rounded hover:bg-gray-100 ${
                isPredefinedFilterSelected('thisWeek')
                  ? 'border border-blue-200 bg-blue-50 font-bold'
                  : ''
              }`}
              onClick={() =>
                handlePredefinedFilterClick('thisWeek', thisWeekFilter)
              }
            >
              <i className="pi pi-eye text-gray-500"></i>
              <span>This Week</span>
            </div>
            <div
              className={`flex cursor-pointer items-center gap-2 rounded hover:bg-gray-100 ${
                isPredefinedFilterSelected('nextWeek')
                  ? 'border border-blue-200 bg-blue-50 font-bold'
                  : ''
              }`}
              onClick={() =>
                handlePredefinedFilterClick('nextWeek', nextWeekFilter)
              }
            >
              <i className="pi pi-eye text-gray-500"></i>
              <span>Next Week</span>
            </div>
            <div
              className={`flex cursor-pointer items-center gap-2 rounded hover:bg-gray-100 ${
                isPredefinedFilterSelected('lastWeek')
                  ? 'border border-blue-200 bg-blue-50 font-bold'
                  : ''
              }`}
              onClick={() =>
                handlePredefinedFilterClick('lastWeek', lastWeekFilter)
              }
            >
              <i className="pi pi-eye text-gray-500"></i>
              <span>Last Week</span>
            </div>
            <div
              className={`flex cursor-pointer items-center gap-2 rounded hover:bg-gray-100 ${
                isPredefinedFilterSelected('nextMonth')
                  ? 'border border-blue-200 bg-blue-50 font-bold'
                  : ''
              }`}
              onClick={() =>
                handlePredefinedFilterClick('nextMonth', nextMonthFilter)
              }
            >
              <i className="pi pi-eye text-gray-500"></i>
              <span>Next Month</span>
            </div>
            <div
              className={`flex cursor-pointer items-center gap-2 rounded hover:bg-gray-100 ${
                isPredefinedFilterSelected('thisMonth')
                  ? 'border border-blue-200 bg-blue-50 font-bold'
                  : ''
              }`}
              onClick={() =>
                handlePredefinedFilterClick('thisMonth', thisMonthFilter)
              }
            >
              <i className="pi pi-eye text-gray-500"></i>
              <span>This Month</span>
            </div>
            <div
              className={`flex cursor-pointer items-center gap-2 rounded hover:bg-gray-100 ${
                isPredefinedFilterSelected('lastMonth')
                  ? 'border border-blue-200 bg-blue-50 font-bold'
                  : ''
              }`}
              onClick={() =>
                handlePredefinedFilterClick('lastMonth', lastMonthFilter)
              }
            >
              <i className="pi pi-eye text-gray-500"></i>
              <span>Last Month</span>
            </div>
          </div>
        )}
        <div className="">
          {!viewList?.length && (
            <div className="text-center text-gray-400">No Views</div>
          )}
          {viewList?.length > 0 &&
            viewList?.map((view: any) => {
              let viewData;
              let isDynamic = false;
              let dynamicTypes: DynamicDateRangeType[] = [];

              try {
                viewData = JSON.parse(view.view);
                if (
                  viewData.dateRange &&
                  viewData.dateRange.type === 'dynamic'
                ) {
                  isDynamic = true;
                  dynamicTypes =
                    viewData.dateRange.dynamicTypes ??
                    (viewData.dateRange.dynamicType
                      ? [viewData.dateRange.dynamicType]
                      : []);
                }
              } catch {
                viewData = null;
              }

              return (
                <div
                  className={`flex items-center justify-between rounded hover:bg-gray-100 ${
                    view.id === currentViewId
                      ? 'border border-blue-200 bg-blue-50'
                      : ''
                  }`}
                  key={view.id}
                >
                  <div
                    className="flex cursor-pointer items-center gap-2"
                    onClick={() => handleSelectView(view.id)}
                  >
                    <i className="pi pi-eye text-gray-500"></i>
                    <span
                      className={view.id === currentViewId ? 'font-bold' : ''}
                    >
                      {view.name}
                    </span>
                    {isDynamic && (
                      <i
                        className="pi pi-clock text-blue-500"
                        title={`Dynamic: ${dynamicTypes.map((t) => getDynamicDateRangeLabel(t)).join(', ')}`}
                      ></i>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className={`rounded border-none bg-transparent p-1 hover:bg-yellow-100 ${
                        view.isDefault === true
                          ? 'text-yellow-500'
                          : 'text-gray-400'
                      }`}
                      onClick={() => handleDefaultView(view.id)}
                      title="Make Default"
                    >
                      <i
                        className={`pi ${view.isDefault === true ? 'pi-star-fill' : 'pi-star'}`}
                      ></i>
                    </button>
                    <button
                      className="rounded border-none bg-transparent p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                      onClick={() => showDeleteViewDialog(view.id)}
                      title="Delete"
                    >
                      <i className="pi pi-trash"></i>
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
        <div className="space-y-2 border-t border-gray-300 pt-4">
          <button
            className="w-full rounded border-none bg-transparent px-2 py-1 text-left font-medium hover:bg-gray-100"
            onClick={() => onClick('displayAddView')}
          >
            Save Filters as New View
          </button>
          <button
            className={`w-full rounded border-none bg-transparent px-2 py-1 text-left font-medium hover:bg-gray-100 ${
              !currentViewId ? 'cursor-not-allowed text-gray-400' : ''
            }`}
            onClick={showUpdateViewDialog}
            disabled={!currentViewId}
          >
            Save Filters to Current View
          </button>
          <button
            className="w-full rounded border-none bg-transparent px-2 py-1 text-left font-medium text-red-600 hover:bg-gray-100 hover:text-red-700"
            onClick={handleClearAllFilters}
          >
            Clear All Filters
          </button>
          <button
            className="w-full rounded border-none bg-transparent px-2 py-1 text-left font-medium text-red-600 hover:bg-gray-100 hover:text-red-700"
            onClick={handleClearLocalStorage}
          >
            Clear Local Storage
          </button>
        </div>
      </div>

      <Dialog
        ref={addViewRef}
        header="New View"
        visible={displayAddView}
        footer={AddFooter('displayAddView')}
        onHide={() => onHide('displayAddView')}
        onShow={() => {
          setAddViewContainer(addViewRef.current?.getElement());
          focusOnFirstInput();
        }}
        className="w-[500px]"
      >
        <div>
          <div className="p-inputgroup mb-3">
            <span className="p-inputgroup-addon">
              <i className="pi pi-eye"></i>
            </span>
            <InputText
              placeholder="Name"
              autoFocus={true}
              value={addViewName}
              onChange={({ target: { value } }) => setAddViewName(value)}
            />
          </div>

          {isShippingDynamicDateEnabled && hasDateFilter() && (
            <div className="mb-3">
              <label className="mb-2 block text-sm font-medium">
                Date Range Type
              </label>
              <div className="mb-2 rounded border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm text-blue-800">
                  <strong>You have a date filter applied.</strong> Choose how
                  you want to save it:
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-start">
                  <RadioButton
                    inputId="fixed-dates"
                    name="dateRangeType"
                    value="fixed"
                    checked={dateRangeType === 'fixed'}
                    onChange={(e) => setDateRangeType(e.value)}
                    className="mt-1"
                  />
                  <div className="ml-2">
                    <label htmlFor="fixed-dates" className="block font-medium">
                      Fixed Dates
                    </label>
                    <p className="text-sm text-gray-600">
                      Save the exact date range you have now (ex: June 14-21,
                      2023)
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <RadioButton
                    inputId="dynamic-dates"
                    name="dateRangeType"
                    value="dynamic"
                    checked={dateRangeType === 'dynamic'}
                    onChange={(e) => setDateRangeType(e.value)}
                    className="mt-1"
                  />
                  <div className="ml-2">
                    <label
                      htmlFor="dynamic-dates"
                      className="block font-medium"
                    >
                      Dynamic Range
                    </label>
                    <p className="text-sm text-gray-600">
                      Automatically updates to the current week/month when you
                      use this filter
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isShippingDynamicDateEnabled &&
            hasDateFilter() &&
            dateRangeType === 'dynamic' && (
              <DynamicDateRangeSelect
                container={addViewContainer}
                value={dynamicDateType}
                onChange={setDynamicDateType}
              />
            )}

          {hasCustomViewsWritePermission && (
            <div className="mb-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={shareWithApp}
                  onChange={(e) => setShareWithApp(e.target.checked)}
                  className="h-4 w-4"
                />
                <span className="text-sm font-medium">
                  Share this view with everyone in {primary}
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                All users will see this view as an available option
              </p>
            </div>
          )}
        </div>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};
